import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import ListItem from '../../components/ListItem';
import { i18nNS } from '../../i18n';
import routes from '../../pages/routes';
import { toShortID } from '../../utils/helpers';
import CourseWidget from '../CourseWidget';
import NoCoursesCard from './NoCoursesCard';
import { getCourseDescription } from './NoCoursesCard/helpers';
import CourseListSkeleton from './Skeleton';
import { StyledList } from './styles';
import useCourseList, { UseCourseList } from './vm';

export interface CourseListProps extends UseCourseList {
  /** @default false */
  isFetchingCourses?: boolean;
  /** @default false */
  canSeeNoCoursesCard?: boolean;
}

const CourseList = ({ isFetchingCourses, canSeeNoCoursesCard, ...vmProps }: CourseListProps) => {
  const { t } = useTranslation([i18nNS.COURSES, i18nNS.COMMON]);

  const { session, courses, handleClickUnpublishedCourse } = useCourseList(vmProps);

  if (isFetchingCourses) {
    return <CourseListSkeleton />;
  }

  if (canSeeNoCoursesCard && courses.length === 0) {
    return <NoCoursesCard />;
  }

  return (
    <StyledList
      type="accessible"
      numItems={courses.length}
      getActiveItemId={(focusIndex) => (courses[focusIndex] ? `course-${courses[focusIndex].id}` : undefined)}
      aria-label={t('list_of_your_ongoing_courses', { ns: i18nNS.COURSES })}
    >
      {(focusIndex) =>
        courses.map((course, index) => (
          <ListItem key={course.id} isFocused={focusIndex === index}>
            <div className="sr-only" id={`course-description-${course.id}`} aria-hidden>
              {getCourseDescription(course)}
            </div>
            {course.status.isSchedulePublished ? (
              <CourseWidget
                course={course}
                component={Link}
                to={routes.courseTimeline.url({
                  universitySlug: session.university.slug,
                  courseShortId: toShortID(course.id),
                })}
                tabIndex={-1}
                id={`course-${course.id}`}
                role="button"
                aria-label={t('code_colon_title', {
                  ns: i18nNS.COMMON,
                  code: course.code,
                  title: course.title,
                })}
                aria-describedby={`course-description-${course.id}`}
              />
            ) : (
              <CourseWidget
                course={course}
                onClick={handleClickUnpublishedCourse(course.id, course.permissions.isPro)}
                tabIndex={-1}
                id={`course-${course.id}`}
                role="button"
                aria-label={t('code_colon_title', {
                  ns: i18nNS.COMMON,
                  code: course.code,
                  title: course.title,
                })}
                aria-describedby={`course-description-${course.id}`}
              />
            )}
          </ListItem>
        ))
      }
    </StyledList>
  );
};

export default CourseList;
